import { useEffect, useRef } from "react";

const colors = [
  "#ff6b6b",
  "#f06595",
  "#f9ca24",
  "#6c5ce7",
  "#00cec9",
  "#81ecec",
];

const spacing = 48; // espacio entre letras

const BirthdayFireworksCanvas = ({ names }) => {
  const canvasRef = useRef(null);
  const nameBlocks = useRef([]);
  const particles = useRef([]);
  const balloons = useRef([]);
  const width = window.innerWidth;
  const height = window.innerHeight;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    canvas.width = width;
    canvas.height = height;

    // Crear partículas explosivas
    const createParticles = (x, y, color) => {
      for (let i = 0; i < 36; i++) {
        const angle = (i * Math.PI * 2) / 36;
        particles.current.push({
          x,
          y,
          vx: Math.cos(angle) * (Math.random() * 5 + 2),
          vy: Math.sin(angle) * (Math.random() * 5 + 2),
          gravity: 0.1,
          life: 80,
          radius: Math.random() * 2 + 1.5,
          color,
        });
      }
    };

    // Crear globos flotantes
    const createBalloon = () => {
      return {
        x: Math.random() * width,
        y: height + Math.random() * 200,
        r: 20 + Math.random() * 10,
        vy: 0.5 + Math.random(),
        color: colors[Math.floor(Math.random() * colors.length)],
        floatAngle: Math.random() * Math.PI * 2,
        floatSpeed: Math.random() * 0.02 + 0.01,
      };
    };

    // Inicializar nombres con posiciones aleatorias
    nameBlocks.current = names.map((name) => {
      const letters = name.trim().split("");
      const totalWidth = letters.length * spacing;

      // Posición inicial aleatoria (todo el nombre junto)
      const startX = Math.random() * (width - totalWidth) + totalWidth / 2;
      const startY = Math.random() * (height - 100) + 50;

      // Posición final aleatoria para desaparecer (zona superior)
      const endX = Math.random() * (width - totalWidth) + totalWidth / 2;
      const endY = Math.random() * height * 0.3 + 30;

      return {
        name,
        letters,
        x: startX,
        y: startY,
        endX,
        endY,
        delay: Math.floor(Math.random() * 180), // delay antes de moverse a desaparecer
        movingToDisappear: false,
        exploded: false,
        alpha: 1,
        life: 120, // tiempo visible antes de empezar a moverse
        color: colors[Math.floor(Math.random() * colors.length)],
        lightColor: (l) =>
          `hsl(${Math.floor(Math.random() * 360)}, 80%, ${l}%)`,
      };
    });

    for (let i = 0; i < 30; i++) {
      balloons.current.push(createBalloon());
    }

    const draw = () => {
      ctx.clearRect(0, 0, width, height);

      // Globos
      balloons.current.forEach((b) => {
        b.y -= b.vy;
        b.floatAngle += b.floatSpeed;
        const floatX = Math.sin(b.floatAngle) * 10;

        ctx.beginPath();
        ctx.ellipse(b.x + floatX, b.y, b.r * 0.6, b.r, 0, 0, Math.PI * 2);
        ctx.fillStyle = b.color;
        ctx.fill();
        ctx.beginPath();
        ctx.moveTo(b.x + floatX, b.y + b.r);
        ctx.lineTo(b.x + floatX, b.y + b.r + 30);
        ctx.strokeStyle = "#444";
        ctx.stroke();

        if (b.y + b.r < -30) Object.assign(b, createBalloon());
      });

      // Dibujar nombres completos
      nameBlocks.current.forEach((block, idx) => {
        if (block.delay > 0) {
          block.delay--;
          return;
        }

        if (!block.movingToDisappear && !block.exploded) {
          if (block.life > 0) {
            block.life--;
          } else {
            block.movingToDisappear = true;
          }
        }

        if (block.movingToDisappear && !block.exploded) {
          const speed = 1.5;
          const dx = block.endX - block.x;
          const dy = block.endY - block.y;
          const dist = Math.sqrt(dx * dx + dy * dy);

          if (dist > speed) {
            block.x += (dx / dist) * speed;
            block.y += (dy / dist) * speed;
          } else {
            block.exploded = true;
            // Explosión en el centro del nombre
            createParticles(block.x, block.y, block.color);
          }
        }

        if (!block.exploded) {
          ctx.save();
          ctx.font = "bold 48px 'Segoe UI', 'Arial Black', sans-serif";
          ctx.textAlign = "center";

          // Dibujar cada letra con separación centrada en block.x, block.y
          block.letters.forEach((char, i) => {
            const letterX =
              block.x -
              ((block.letters.length - 1) * spacing) / 2 +
              i * spacing;
            const letterY = block.y;

            ctx.shadowColor = block.color;
            ctx.shadowBlur = 40;
            ctx.fillStyle = block.color;

            ctx.globalAlpha = block.alpha;

            ctx.fillText(char, letterX, letterY);
            ctx.lineWidth = 3;
            ctx.strokeStyle = "#ffffffcc";
            ctx.strokeText(char, letterX, letterY);

            ctx.shadowBlur = 20;
            ctx.shadowColor = "white";
            ctx.fillText(char, letterX, letterY);
          });

          ctx.restore();
          ctx.globalAlpha = 1;
        }
      });

      // Partículas
      particles.current.forEach((p, i) => {
        p.x += p.vx;
        p.y += p.vy;
        p.vy += p.gravity;
        p.life--;
        ctx.globalAlpha = p.life / 80;
        ctx.fillStyle = p.color;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.radius, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
        if (p.life <= 0) {
          particles.current.splice(i, 1);
        }
      });

      requestAnimationFrame(draw);
    };

    draw();
  }, [names]);

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "100%",
        zIndex: 10,
        pointerEvents: "none",
      }}
    />
  );
};

export default BirthdayFireworksCanvas;
